import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import {
  vertexShader,
  grassFragmentShader,
  waterFragmentShader,
  cloudFragmentShader,
  starFragmentShader
} from './shaders';

import bgGrass from '../../images/bg_grass_light.webp';
import bgGrassDark from '../../images/bg_grass_dark.webp';
import bgWater from '../../images/bg_water_light.webp';
import bgWaterDark from '../../images/bg_water_dark.webp';
import bgSky from '../../images/bg_sky_light.webp';
import bgSkyDark from '../../images/bg_sky_dark.webp';

import bgMobileGrass from '../../images/bg_mobile_grass_light.webp';
import bgMobileGrassDark from '../../images/bg_mobile_grass_dark.webp';
import bgMobileWater from '../../images/bg_mobile_water_light.webp';
import bgMobileWaterDark from '../../images/bg_mobile_water_dark.webp';
import bgMobileSky from '../../images/bg_mobile_sky_light.webp';
import bgMobileSkyDark from '../../images/bg_mobile_sky_dark.webp';

interface ShaderBackgroundProps {
  mode: 'day' | 'night' | '';
  isMobile: boolean;
}

type LayerKey = 'grass' | 'water' | 'sky';

const ShaderBackground: React.FC<ShaderBackgroundProps> = ({ mode, isMobile }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: false,
        alpha: false,
        powerPreference: 'low-power'
      });
    } catch {
      return;
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1 : 1.5));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 1);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const isNight = mode === 'night';
    const skyFrag = isNight ? starFragmentShader : cloudFragmentShader;

    const loader = new THREE.TextureLoader();
    const loadTexture = (url: string, repeat: boolean): THREE.Texture => {
      const tex = loader.load(url);
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      tex.wrapS = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      return tex;
    };

    const pickImg = (desktop: string, mobile: string) => (isMobile ? mobile : desktop);
    const grassSrc = isNight ? pickImg(bgGrassDark, bgMobileGrassDark) : pickImg(bgGrass, bgMobileGrass);
    const waterSrc = isNight ? pickImg(bgWaterDark, bgMobileWaterDark) : pickImg(bgWater, bgMobileWater);
    const skySrc   = isNight ? pickImg(bgSkyDark, bgMobileSkyDark) : pickImg(bgSky, bgMobileSky);

    const layers: Record<
      LayerKey,
      { mesh: THREE.Mesh; material: THREE.ShaderMaterial; texture: THREE.Texture }
    > = {
      sky: buildLayer(loadTexture(skySrc, true), skyFrag, 0, true),
      water: buildLayer(loadTexture(waterSrc, true), waterFragmentShader, 1),
      grass: buildLayer(loadTexture(grassSrc, false), grassFragmentShader, 2)
    };

    function buildLayer(
      texture: THREE.Texture,
      fragmentShader: string,
      renderOrder: number,
      opaque: boolean = false
    ) {
      const material = new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          uTexture: { value: texture },
          uTime: { value: 0 }
        },
        transparent: !opaque,
        depthTest: false,
        depthWrite: false
      });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
      mesh.renderOrder = renderOrder;
      scene.add(mesh);
      return { mesh, material, texture };
    }

    const handleResize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.render(scene, camera);
    };
    window.addEventListener('resize', handleResize);

    // Pause when the root element gets `.paused` or `.browser-open` class.
    const root = document.getElementById('mawmawmaw');
    let paused = false;
    const updatePaused = () => {
      if (!root) return;
      paused = root.classList.contains('paused') || root.classList.contains('browser-open');
    };
    updatePaused();
    const observer = root
      ? new MutationObserver(updatePaused)
      : null;
    if (observer && root) {
      observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    }

    let rafId = 0;
    let lastT = performance.now();
    const tick = (now: number) => {
      const delta = (now - lastT) / 1000;
      lastT = now;
      if (!paused) {
        const t = layers.sky.material.uniforms.uTime.value + delta;
        layers.sky.material.uniforms.uTime.value = t;
        layers.water.material.uniforms.uTime.value = t;
        layers.grass.material.uniforms.uTime.value = t;
        renderer.render(scene, camera);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      observer?.disconnect();
      (Object.keys(layers) as LayerKey[]).forEach((k) => {
        const l = layers[k];
        scene.remove(l.mesh);
        l.mesh.geometry.dispose();
        l.material.dispose();
        l.texture.dispose();
      });
      renderer.dispose();
    };
  }, [mode, isMobile]);

  return <canvas ref={canvasRef} className="shader-background-canvas" aria-hidden="true" />;
};

export default React.memo(ShaderBackground);
